import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../AxiosInstance";
import usePolling from "../../../../../hooks/usePolling";
import { formatTimestamp } from "../../../../../utils/utils";
import { isAdmin } from "../../../../../utils/roles";
import styles from "./params_section.module.css";

const POLL_MS = 15000;
const GROUP_ORDER = ["electrical", "temperature", "ina", "device", "network"];

function sameValue(a, b) {
  if (a === null || a === undefined || a === "") return b === null || b === undefined || b === "";
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
  if (typeof a === "boolean" || typeof b === "boolean") return Boolean(a) === Boolean(b);
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return String(a) === String(b);
}

function displayValue(spec, value, t) {
  if (value === null || value === undefined || value === "") return t("params.unset");
  if (spec.type === "bool") return value ? t("params.yes") : t("params.no");
  if (Array.isArray(value)) return JSON.stringify(value);
  return String(value);
}

function Field({ spec, value, onChange, disabled, t }) {
  if (spec.type === "bool") {
    return (
      <label className={styles.checkbox}>
        <input type="checkbox" checked={Boolean(value)} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        {value ? t("params.yes") : t("params.no")}
      </label>
    );
  }
  if (spec.type === "choice") {
    return (
      <select className={styles.input} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {(spec.choices || []).map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    );
  }
  if (spec.type === "list_int" || spec.type === "limits") {
    return (
      <input
        className={`${styles.input} ${styles.inputWide}`}
        value={value === null || value === undefined ? "" : Array.isArray(value) ? JSON.stringify(value) : String(value)}
        disabled={disabled}
        placeholder={spec.type === "limits" ? "[[-30, 80], [-30, 80]]" : "[1, 2, 3]"}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      className={styles.input}
      type="number"
      step={spec.type === "int" ? 1 : "any"}
      min={spec.min}
      max={spec.max}
      value={value === null || value === undefined ? "" : value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Parámetros de kpdu.conf del equipo, editables por administradores. El
 * equipo publica su inventario (valores + esquema); al guardar, la nube lo
 * manda por MQTT y el equipo confirma volviendo a publicar el inventario.
 */
export default function ParamsSection({ pdui_id }) {
  const { t, i18n } = useTranslation();
  const admin = isAdmin();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [draft, setDraft] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  usePolling(
    (isActive) =>
      axiosInstance
        .get(`pdui/${pdui_id}/device-params/`)
        .then((r) => {
          if (!isActive()) return;
          setData(r.data);
          setError(false);
        })
        .catch(() => {
          if (isActive()) setError(true);
        }),
    POLL_MS,
    true,
    pdui_id,
  );

  const reported = data?.reported || {};
  const params = reported.params || {};
  const schema = reported.schema || [];
  const pending = data?.pending?.changes || {};
  const groups = reported.groups || {};

  const labelOf = (spec) => {
    const key = `params.keys.${spec.key}.label`;
    return i18n.exists(key) ? t(key) : spec.label || spec.key;
  };
  const helpOf = (spec) => {
    const key = `params.keys.${spec.key}.help`;
    return i18n.exists(key) ? t(key) : spec.help || "";
  };
  const groupName = (g) => {
    const key = `params.groups.${g}`;
    return i18n.exists(key) ? t(key) : groups[g] || g;
  };

  const changes = {};
  schema.forEach((spec) => {
    if (spec.key in draft && !sameValue(draft[spec.key], params[spec.key])) changes[spec.key] = draft[spec.key];
  });
  const changedKeys = Object.keys(changes);
  const restartNeeded = changedKeys.some((k) => (schema.find((s) => s.key === k) || {}).restart !== false);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    setFieldErrors({});
    try {
      const r = await axiosInstance.put(`pdui/${pdui_id}/device-params/`, { changes });
      setData((prev) => ({ ...(prev || {}), ...r.data }));
      setDraft({});
      setConfirming(false);
      setMessage({
        type: r.data.delivered_to_device ? "success" : "warning",
        text: r.data.delivered_to_device
          ? (restartNeeded ? t("params.savedRestart") : t("params.saved"))
          : t("params.savedNotDelivered"),
      });
    } catch (err) {
      const body = err.response?.data || {};
      setFieldErrors(body.errors || {});
      setMessage({ type: "error", text: body.error || t("params.saveError") });
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const pendingAction = async (flag) => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await axiosInstance.put(`pdui/${pdui_id}/device-params/`, { [flag]: true });
      setData((prev) => ({ ...(prev || {}), ...r.data }));
      setMessage({
        type: flag === "discard" || r.data.delivered_to_device ? "success" : "warning",
        text: flag === "discard" ? t("params.discarded") : r.data.delivered_to_device ? t("params.resent") : t("params.savedNotDelivered"),
      });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || t("params.saveError") });
    } finally {
      setSaving(false);
    }
  };

  const byGroup = {};
  schema.forEach((spec) => {
    (byGroup[spec.group || "device"] = byGroup[spec.group || "device"] || []).push(spec);
  });
  const orderedGroups = [...GROUP_ORDER.filter((g) => byGroup[g]), ...Object.keys(byGroup).filter((g) => !GROUP_ORDER.includes(g))];

  return (
    <div className={styles.container}>
      <div className={styles.header}>{t("params.title")}</div>
      <div className={styles.help}>{t("params.help")}</div>
      {error && <div className={styles.msg_error}>{t("params.error")}</div>}
      {data && !data.has_inventory && <div className={styles.empty}>{t("params.noInventory")}</div>}
      {data?.has_inventory && (
        <div className={styles.meta}>
          {t("params.reportedAt", { when: formatTimestamp(reported.received_at || reported.at) })}
          {reported.restart_pending && <span className={styles.badgeWarn}>{t("params.restarting")}</span>}
          {Object.keys(pending).length > 0 && (
            <>
              <span className={styles.badgePending}>{t("params.pendingCount", { n: Object.keys(pending).length })}</span>
              {admin && (
                <>
                  <button type="button" className={styles.miniButton} disabled={saving} onClick={() => pendingAction("resend")}>
                    {t("params.resend")}
                  </button>
                  <button type="button" className={styles.miniButton} disabled={saving} onClick={() => pendingAction("discard")}>
                    {t("params.discardPending")}
                  </button>
                </>
              )}
            </>
          )}
          {!admin && <span className={styles.readOnly}>{t("params.readOnly")}</span>}
        </div>
      )}
      {reported.last_apply && Object.keys(reported.last_apply.errors || {}).length > 0 && (
        <div className={styles.msg_warning}>
          {t("params.deviceRejected")}
          <ul className={styles.errorList}>
            {Object.entries(reported.last_apply.errors).map(([k, v]) => <li key={k}><b>{k}</b>: {v}</li>)}
          </ul>
        </div>
      )}
      {message && <div className={styles[`msg_${message.type}`]}>{message.text}</div>}

      {orderedGroups.map((g) => (
        <div key={g} className={styles.group}>
          <div className={styles.groupTitle}>{groupName(g)}</div>
          <div className={styles.list}>
            {byGroup[g].map((spec) => {
              const current = params[spec.key];
              const value = spec.key in draft ? draft[spec.key] : current;
              const isPending = spec.key in pending;
              const dirty = spec.key in changes;
              return (
                <div key={spec.key} className={`${styles.row} ${dirty ? styles.rowDirty : ""}`}>
                  <div className={styles.rowMain}>
                    <div className={styles.rowLabel}>
                      {labelOf(spec)}
                      {spec.restart !== false && <span className={styles.badgeRestart} title={t("params.restartHint")}>{t("params.restart")}</span>}
                      {isPending && <span className={styles.badgePending} title={t("params.pendingHint")}>{t("params.pending", { v: displayValue(spec, pending[spec.key], t) })}</span>}
                    </div>
                    {helpOf(spec) && <div className={styles.rowHelp}>{helpOf(spec)}</div>}
                    <div className={styles.rowMeta}>
                      {t("params.current", { v: displayValue(spec, current, t) })}
                      {spec.default !== null && spec.default !== undefined && ` · ${t("params.default", { v: displayValue(spec, spec.default, t) })}`}
                      {(spec.min !== undefined || spec.max !== undefined) && spec.type !== "choice" && ` · ${spec.min ?? "…"} – ${spec.max ?? "…"}`}
                    </div>
                    {fieldErrors[spec.key] && <div className={styles.fieldError}>{fieldErrors[spec.key]}</div>}
                  </div>
                  <div className={styles.rowField}>
                    <Field spec={spec} value={value} disabled={!admin || saving} t={t}
                      onChange={(v) => setDraft((d) => ({ ...d, [spec.key]: v }))} />
                    <code className={styles.key}>{spec.key}</code>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {admin && data?.has_inventory && (
        <div className={styles.footer}>
          {!confirming ? (
            <>
              <button type="button" className={styles.saveButton} disabled={changedKeys.length === 0 || saving} onClick={() => setConfirming(true)}>
                {t("params.save", { n: changedKeys.length })}
              </button>
              {changedKeys.length > 0 && (
                <button type="button" className={styles.linkButton} onClick={() => setDraft({})}>{t("params.discard")}</button>
              )}
            </>
          ) : (
            <div className={styles.confirmBox}>
              <div className={styles.confirmTitle}>{t("params.confirmTitle")}</div>
              <ul className={styles.confirmList}>
                {changedKeys.map((k) => {
                  const spec = schema.find((s) => s.key === k) || { key: k };
                  return <li key={k}><b>{labelOf(spec)}</b>: {displayValue(spec, params[k], t)} → {displayValue(spec, changes[k], t)}</li>;
                })}
              </ul>
              {restartNeeded && <div className={styles.confirmWarn}>{t("params.confirmRestart")}</div>}
              <div className={styles.confirmActions}>
                <button type="button" className={styles.saveButton} disabled={saving} onClick={save}>
                  {saving ? t("params.saving") : t("params.confirm")}
                </button>
                <button type="button" className={styles.linkButton} disabled={saving} onClick={() => setConfirming(false)}>{t("params.cancel")}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
